import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, RefreshCw, ExternalLink, Trash2, Check, AlertTriangle } from "lucide-react";
import { API } from "@/lib/auth";

/** Admin panel for connecting an Instagram Graph API long-lived token.
 * User pastes a token from the Meta Graph API Explorer; we fetch + cache posts. */
export default function InstagramConnectPanel() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState({ access_token: "", handle: "malekdridihairandbeauty", limit: 6, ig_user_id: "" });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/instagram/config`);
      setConfig(r.data);
      if (r.data?.handle) setForm((f) => ({ ...f, handle: r.data.handle }));
      if (r.data?.limit) setForm((f) => ({ ...f, limit: r.data.limit }));
      if (r.data?.ig_user_id) setForm((f) => ({ ...f, ig_user_id: r.data.ig_user_id }));
    } catch (e) {
      toast.error("Could not load Instagram settings");
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.access_token || form.access_token.length < 20) {
      return toast.error("Paste a long-lived Instagram access token first");
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/instagram/token`, {
        access_token: form.access_token.trim(),
        handle: form.handle.trim().replace(/^@/, ""),
        limit: Number(form.limit) || 6,
        ig_user_id: form.ig_user_id.trim() || undefined,
      });
      toast.success(`Connected — pulled ${(r.data.posts || []).length} recent posts`);
      setForm((f) => ({ ...f, access_token: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect Instagram");
    } finally { setSaving(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/admin/instagram/refresh`);
      const n = (r.data.posts || []).length;
      const mNew = r.data.last_mirrored_new || 0;
      toast.success(
        mNew > 0
          ? `Refreshed — ${n} posts pulled, ${mNew} new image${mNew === 1 ? "" : "s"} self-hosted`
          : `Refreshed — ${n} posts pulled`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram? The homepage will fall back to the placeholder tiles until you reconnect.")) return;
    try {
      await axios.delete(`${API}/admin/instagram/token`);
      toast.success("Instagram disconnected");
      await load();
    } catch (e) {
      toast.error("Disconnect failed");
    }
  };

  if (!config) {
    return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading Instagram settings…</div>;
  }

  const daysSinceSync = config.last_synced_at
    ? Math.floor((Date.now() - new Date(config.last_synced_at).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="space-y-6" data-testid="admin-instagram-panel">
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline flex items-center gap-2"><Instagram size={14} /> Instagram feed</div>
            <p className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1 max-w-lg">
              We use the Instagram Graph API to pull your latest posts. Every synced photo is <strong>automatically mirrored to your own storage</strong> so links never expire. You'll only need to renew your Meta access token roughly every <strong>60 days</strong>.
            </p>
          </div>
          {config.connected ? (
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--gold)/0.15)] text-[hsl(var(--onyx))]">
              <Check size={12} /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--onyx)/0.06)] text-[hsl(var(--onyx)/0.55)]">
              Not connected
            </span>
          )}
        </div>

        {config.connected && (
          <div className="grid gap-3 sm:grid-cols-4">
            <Info label="Handle" value={config.handle ? `@${config.handle}` : "—"} />
            <Info label="Cached posts" value={String((config.posts || []).length)} />
            <Info label="Self-hosted" value={`${config.mirrored_count ?? (config.posts || []).filter((p) => p.mirrored).length} / ${(config.posts || []).length}`} />
            <Info
              label="Last sync"
              value={config.last_synced_at ? new Date(config.last_synced_at).toLocaleString() : "—"}
              warning={daysSinceSync !== null && daysSinceSync > 45}
            />
          </div>
        )}

        {daysSinceSync !== null && daysSinceSync > 45 && (
          <div className="rounded-2xl bg-[hsl(var(--gold)/0.1)] border border-[hsl(var(--gold)/0.35)] p-4 flex items-start gap-3">
            <AlertTriangle size={16} className="text-[hsl(var(--gold))] mt-0.5 shrink-0" />
            <div className="text-xs text-[hsl(var(--onyx)/0.8)] leading-relaxed">
              <strong>Heads up:</strong> your Instagram token was saved {daysSinceSync} days ago — long-lived tokens usually expire around 60 days.
              Head to the <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="underline">Meta Graph API Explorer</a>, extend/refresh your access token and paste it below.
            </div>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Paste your token</div>
        <details className="rounded-2xl bg-blush/30 border border-[hsl(var(--onyx)/0.08)] p-4 text-xs text-[hsl(var(--onyx)/0.75)]">
          <summary className="cursor-pointer text-sm font-medium">How to get a long-lived Instagram token (10 minutes)</summary>
          <ol className="mt-3 space-y-2 list-decimal ml-5 leading-relaxed">
            <li>Open <a className="underline" href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">developers.facebook.com/tools/explorer/</a> and sign in with the Facebook account that manages your salon Page.</li>
            <li>In the top-right, pick your Meta App (or "Graph API Explorer" default).</li>
            <li>Under "User or Page", pick <strong>User token</strong>.</li>
            <li>Click <strong>"Add permissions"</strong> and enable: <code className="bg-white px-1 rounded">instagram_basic</code>, <code className="bg-white px-1 rounded">pages_show_list</code>, <code className="bg-white px-1 rounded">pages_read_engagement</code>.</li>
            <li>Click <strong>"Generate Access Token"</strong> and authorise. The token appears in the "Access Token" field.</li>
            <li>Copy it, then click the little <strong>ℹ️ info icon</strong> next to the token → <strong>"Open in Access Token Tool"</strong> → <strong>"Extend Access Token"</strong>. Copy the new (long-lived) token.</li>
            <li>Paste it below and hit <strong>Connect</strong>.</li>
          </ol>
        </details>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Long-lived access token</label>
          <textarea
            value={form.access_token}
            onChange={(e) => setForm({ ...form, access_token: e.target.value })}
            rows={3}
            data-testid="ig-token-input"
            placeholder="EAA... paste your long-lived token here"
            className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-xs font-mono outline-none focus:border-[hsl(var(--onyx))] resize-y"
          />
          {config.token_hint && (
            <div className="text-[10px] text-[hsl(var(--onyx)/0.5)] mt-1">
              Current token: <code>{config.token_hint}</code> (saved {new Date(config.token_saved_at || Date.now()).toLocaleDateString()})
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Handle</label>
            <input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.replace(/^@/, "") })}
              placeholder="malekdridihairandbeauty" data-testid="ig-handle"
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Posts to show</label>
            <input type="number" min="1" max="12" value={form.limit}
              onChange={(e) => setForm({ ...form, limit: Number(e.target.value) })}
              data-testid="ig-limit"
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
              IG user ID (auto-detected if empty)
            </label>
            <input value={form.ig_user_id} onChange={(e) => setForm({ ...form, ig_user_id: e.target.value })}
              placeholder="Optional — we'll find it"
              className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm" />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button onClick={connect} disabled={saving || !form.access_token} data-testid="ig-connect"
            className="btn-pill btn-primary disabled:opacity-50">
            <Instagram size={14} /> {saving ? "Connecting…" : config.connected ? "Update token" : "Connect"}
          </button>
          {config.connected && (
            <>
              <button onClick={refresh} disabled={refreshing} data-testid="ig-refresh"
                className="btn-pill btn-outline">
                <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                {refreshing ? "Refreshing…" : "Refresh feed now"}
              </button>
              <button onClick={disconnect} data-testid="ig-disconnect"
                className="btn-pill btn-outline">
                <Trash2 size={14} /> Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {(config.posts || []).length > 0 && (
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
          <div className="overline">Cached posts (this is what shows on the homepage)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="ig-posts-preview">
            {config.posts.map((p) => (
              <a key={p.id} href={p.link} target="_blank" rel="noreferrer"
                className="block group aspect-square rounded-2xl overflow-hidden bg-blush/40 relative">
                <img src={p.image} alt={p.caption?.slice(0, 40) || "Instagram post"}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <ExternalLink size={14} className="text-white" />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, warning }) {
  return (
    <div className={`rounded-2xl p-4 border ${warning ? "bg-[hsl(var(--gold)/0.08)] border-[hsl(var(--gold)/0.3)]" : "bg-blush/30 border-[hsl(var(--onyx)/0.08)]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</div>
      <div className={`text-sm mt-1 truncate ${warning ? "text-[hsl(var(--burgundy))]" : "text-[hsl(var(--onyx))]"}`}>{value}</div>
    </div>
  );
}
